/*
 * Calculadora de frete na página de produto.
 * Cria um carrinho auxiliar na Storefront API (acesso sem token) só com o produto exibido e lê as
 * opções de entrega. O carrinho do cliente não é alterado e os valores são os mesmos do checkout.
 */
(() => {
  if (customElements.get('product-shipping-calculator')) return;

  const API_VERSION = '2026-07';
  const STORAGE_KEY = 'acqualive:cep';

  // Faixas de CEP por estado (Correios), pelos cinco primeiros dígitos.
  const ZIP_RANGES = [
    ['SP', 1000, 19999], ['RJ', 20000, 28999], ['ES', 29000, 29999], ['MG', 30000, 39999],
    ['BA', 40000, 48999], ['SE', 49000, 49999], ['PE', 50000, 56999], ['AL', 57000, 57999],
    ['PB', 58000, 58999], ['RN', 59000, 59999], ['CE', 60000, 63999], ['PI', 64000, 64999],
    ['MA', 65000, 65999], ['PA', 66000, 68899], ['AP', 68900, 68999], ['AM', 69000, 69299],
    ['RR', 69300, 69399], ['AM', 69400, 69899], ['AC', 69900, 69999], ['DF', 70000, 72799],
    ['GO', 72800, 72999], ['DF', 73000, 73699], ['GO', 73700, 76799], ['RO', 76800, 76999],
    ['TO', 77000, 77999], ['MT', 78000, 78899], ['RO', 78900, 78999], ['MS', 79000, 79999],
    ['PR', 80000, 87999], ['SC', 88000, 89999], ['RS', 90000, 99999]
  ];

  const CART_CREATE = `mutation ProductShippingCart($input: CartInput!) {
    cartCreate(input: $input) {
      cart { id }
      userErrors { field code message }
    }
  }`;

  // Tarifas de transportadora (Frenet, Intelipost etc.) só vêm com withCarrierRates, que exige @defer.
  const CART_DELIVERY_OPTIONS = `query ProductShippingOptions($id: ID!) {
    cart(id: $id) {
      id
      ...DeliveryOptions @defer
    }
  }
  fragment DeliveryOptions on Cart {
    deliveryGroups(first: 5, withCarrierRates: true) {
      nodes {
        deliveryOptions {
          title
          description
          estimatedCost { amount currencyCode }
        }
      }
    }
  }`;

  const onlyDigits = (value) => String(value || '').replace(/\D/g, '').slice(0, 8);

  const formatZip = (value) => {
    const digits = onlyDigits(value);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  };

  const provinceFromZip = (zip) => {
    const prefix = parseInt(zip.slice(0, 5), 10);
    const range = ZIP_RANGES.find(([, min, max]) => prefix >= min && prefix <= max);
    return range ? range[0] : null;
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[char]);

  // Lê respostas JSON comuns e multipart/mixed (@defer), juntando os fragmentos incrementais.
  const readGraphQL = async (response) => {
    const contentType = response.headers.get('content-type') || '';
    const text = await response.text();

    if (!contentType.includes('multipart/mixed')) return JSON.parse(text);

    const boundary = (contentType.match(/boundary="?([^";]+)"?/) || [])[1] || 'graphql';
    const result = { data: {}, errors: [] };

    text.split(`--${boundary}`).forEach((part) => {
      const start = part.indexOf('{');
      if (start === -1) return;

      let payload;
      try {
        payload = JSON.parse(part.slice(start).trim());
      }
      catch (error) {
        return;
      }

      if (payload.data) Object.assign(result.data, payload.data);
      if (payload.errors) result.errors.push(...payload.errors);

      (payload.incremental || []).forEach((increment) => {
        if (increment.errors) result.errors.push(...increment.errors);
        if (!increment.data) return;

        const target = (increment.path || []).reduce((node, key) => {
          node[key] = node[key] || {};
          return node[key];
        }, result.data);

        Object.assign(target, increment.data);
      });
    });

    return result;
  };

  class ProductShippingCalculator extends HTMLElement {
    constructor() {
      super();

      this.cache = new Map();
      this.onSubmit = this.onSubmit.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="zip"]');
      this.submitButton = this.querySelector('button[type="submit"]');
      this.errorElement = this.querySelector('[data-shipping-error]');
      this.resultsElement = this.querySelector('[data-shipping-results]');
      this.strings = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '{}');

      this.form.addEventListener('submit', this.onSubmit);
      this.input.addEventListener('input', this.onInput);

      if (window.theme?.pubsub) {
        this.unsubscribeVariantChange = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }

      try {
        const savedZip = localStorage.getItem(STORAGE_KEY);
        if (savedZip && !this.input.value) this.input.value = formatZip(savedZip);
      }
      catch (error) {
        // Sem localStorage (ex.: navegação privada): o CEP apenas não fica salvo.
      }
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.input?.removeEventListener('input', this.onInput);
      this.unsubscribeVariantChange?.();
      this.abortController?.abort();
    }

    get productForm() {
      return document.getElementById(this.dataset.productFormId);
    }

    get variantId() {
      return this.productForm?.querySelector('[name="id"]')?.value || this.dataset.variantId;
    }

    get quantity() {
      const formId = CSS.escape(this.dataset.productFormId || '');
      const input = document.querySelector(`input[name="quantity"][form="${formId}"]`) || this.productForm?.querySelector('input[name="quantity"]');
      const value = parseInt(input?.value, 10);

      return Number.isNaN(value) || value < 1 ? 1 : value;
    }

    get hasResults() {
      return this.resultsElement.childElementCount > 0;
    }

    onInput() {
      const formatted = formatZip(this.input.value);
      if (formatted !== this.input.value) this.input.value = formatted;
      if (this.input.getAttribute('aria-invalid') === 'true') this.clearError();
    }

    onSubmit(event) {
      event.preventDefault();
      this.calculate();
    }

    onVariantChange(event) {
      if (event?.data?.sectionId !== this.dataset.sectionId || !this.hasResults) return;
      this.calculate();
    }

    async calculate() {
      const zip = onlyDigits(this.input.value);
      const provinceCode = zip.length === 8 ? provinceFromZip(zip) : null;

      if (!provinceCode) {
        this.showError(this.strings.invalidZip);
        this.input.focus();
        return;
      }

      this.clearError();

      try {
        localStorage.setItem(STORAGE_KEY, zip);
      }
      catch (error) {
        // Sem localStorage: segue sem lembrar o CEP.
      }

      const variantId = this.variantId;
      const quantity = this.quantity;
      const cacheKey = `${variantId}:${quantity}:${zip}`;

      if (this.cache.has(cacheKey)) {
        this.renderOptions(this.cache.get(cacheKey), zip, provinceCode);
        return;
      }

      this.abortController?.abort();
      const controller = new AbortController();
      this.abortController = controller;
      this.setLoading(true);

      try {
        const options = await this.fetchOptions({ variantId, quantity, zip, provinceCode, signal: controller.signal });
        this.cache.set(cacheKey, options);
        this.renderOptions(options, zip, provinceCode);
      }
      catch (error) {
        if (error.name === 'AbortError') return;

        console.error(error);
        this.showError(error.userMessage || this.strings.error);
      }
      finally {
        if (this.abortController === controller) this.setLoading(false);
      }
    }

    async request(query, variables, signal) {
      const response = await fetch(`/api/${API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'multipart/mixed, application/json' },
        body: JSON.stringify({ query, variables }),
        signal
      });

      if (!response.ok) throw new Error(`Storefront API respondeu ${response.status}`);

      const result = await readGraphQL(response);
      if (result.errors?.length) throw new Error(result.errors.map((item) => item.message).join('; '));

      return result.data;
    }

    async fetchOptions({ variantId, quantity, zip, provinceCode, signal }) {
      const created = await this.request(CART_CREATE, {
        input: {
          lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity }],
          buyerIdentity: { countryCode: 'BR' },
          delivery: {
            addresses: [{
              selected: true,
              oneTimeUse: true,
              address: { deliveryAddress: { zip: formatZip(zip), countryCode: 'BR', provinceCode } }
            }]
          }
        }
      }, signal);

      const cartId = created?.cartCreate?.cart?.id;

      if (!cartId) {
        const userError = created?.cartCreate?.userErrors?.[0];
        const field = (userError?.field || []).join('.');
        const error = new Error(userError?.message || 'cartCreate não retornou carrinho');

        if (field.includes('lines')) error.userMessage = this.strings.unavailable;
        else if (field.includes('delivery')) error.userMessage = this.strings.invalidZip;

        throw error;
      }

      const rated = await this.request(CART_DELIVERY_OPTIONS, { id: cartId }, signal);

      return (rated?.cart?.deliveryGroups?.nodes || []).flatMap((group) => group.deliveryOptions || []);
    }

    renderOptions(options, zip, provinceCode) {
      if (!options.length) {
        this.resultsElement.innerHTML = `<p class="product-shipping__summary">${escapeHtml(this.strings.noRates)}</p>`;
        return;
      }

      const summary = (this.strings.resultsFor || '')
        .replace('[zip]', formatZip(zip))
        .replace('[province]', provinceCode);

      const items = [...options]
        .sort((a, b) => parseFloat(a.estimatedCost?.amount || 0) - parseFloat(b.estimatedCost?.amount || 0))
        .map((option) => {
          const amount = parseFloat(option.estimatedCost?.amount);
          const price = amount === 0
            ? `<strong>${escapeHtml(this.strings.free)}</strong>`
            : escapeHtml(this.formatMoney(amount, option.estimatedCost?.currencyCode));
          const eta = option.description
            ? `<span class="product-shipping__eta">${escapeHtml(option.description)}</span>`
            : '';

          return `<li class="product-shipping__option"><span class="product-shipping__method">${escapeHtml(option.title)}${eta}</span><span class="product-shipping__price">${price}</span></li>`;
        })
        .join('');

      this.resultsElement.innerHTML = `<p class="product-shipping__summary">${escapeHtml(summary)}</p><ul class="product-shipping__options" role="list">${items}</ul>`;
    }

    formatMoney(amount, currencyCode = 'BRL') {
      if (Number.isNaN(amount)) return '';

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', { style: 'currency', currency: currencyCode }).format(amount);
      }
      catch (error) {
        return `${currencyCode} ${amount.toFixed(2)}`;
      }
    }

    setLoading(isLoading) {
      const label = this.submitButton.querySelector('.btn-text') || this.submitButton;

      if (isLoading) {
        this.defaultLabel = this.defaultLabel || label.textContent;
        this.submitButton.setAttribute('aria-busy', 'true');
        label.textContent = this.submitButton.dataset.loadingLabel || this.defaultLabel;
      }
      else {
        this.submitButton.removeAttribute('aria-busy');
        if (this.defaultLabel) label.textContent = this.defaultLabel;
      }
    }

    showError(message) {
      this.errorElement.textContent = message || '';
      this.errorElement.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
    }

    clearError() {
      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
      this.input.removeAttribute('aria-invalid');
    }
  }

  customElements.define('product-shipping-calculator', ProductShippingCalculator);
})();
